
<template>
  <div class="operation text-center">
    <div v-if="!isDone">
      <div class="counter">
        <div class="counter-cur">{{ iProblem }}</div>
        <div class="counter-total">{{ this.myDrill.config.nProblems }}</div>
      </div>
      <div class="clearfix">
        <div class="score score-wrong">{{ nWrong }}</div>
        <div class="score score-correct">{{ nCorrect }}</div>
      </div>
      <br>
      <!-- question -->
      <div v-for="(content, index) in this.problem.question" :key="'q' + index">
        <div v-if="content.type === 'string'" v-html="content.value" class="message mb-2"></div>
        <div v-if="content.type === 'formula'" v-html="renderTex(content.value)" class="equation mb-2"></div>
        <div v-if="content.type === 'table'" class="my-3"><function-table :values="content.values"></function-table></div>
        <div v-if="content.type === 'graph'" class="my-3"><function-graph :config="content.config" :functions="content.functions"></function-graph></div>
      </div>
      <br>
      <!-- answer -->
      <div v-for="(answer, index) in this.problem.answers" :key="'a' + index" class="equation mb-2">
        <div v-if="answer.before" v-html="renderTex(answer.before)" class="d-inline-block"></div>&nbsp;
        <input type="text" class="d-inline-block text-center"
          :id="'userInput' + index"
          :style="'width: ' + inputWidth + 'em'"
          :class="inputStatus"
          :disabled="!inputAllowed"
          v-model="userInputs[index]"
          @keydown.arrow-up.prevent="moveTo(index - 1)"
          @keydown.arrow-down.prevent="moveTo(index + 1)"
          @keydown.enter.prevent="submitAnswer"/>
        <div v-if="answer.after" v-html="renderTex(answer.after)" class="d-inline-block"></div>&nbsp;
        <br>
      </div>
      <!-- footnote & messages -->
      <div class="mt-3">
        <div v-if="this.problem.footnote" v-html="this.problem.footnote" class="footnote mb-2"></div>
        <div v-for="(message, index) in this.messages" :key="'m' + index" class="message mb-2" v-html="renderTex(message)"></div>
      </div>
      <!-- check & gotit buttons -->
      <b-button ref="btnCheck" variant="info" class="mt-3"
        :disabled="!inputAllowed"
        :class="btnCheckStatus"
        @click.prevent="submitAnswer">Check</b-button>
      <b-button ref="btnGotit" variant="info" class="mt-3"
        :class="btnGotitStatus"
        @click.prevent="newProblem">Got it!</b-button>
      <p class="mt-3"><small>
        You can hit the enter key to check.
      </small></p>
    </div>
    <div v-else class="message">
      <br>
      <b-row>
        <b-col cols="6" class="text-right">Total:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ this.myDrill.config.nProblems }}</b> Problems</b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="text-right">Correct:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ nCorrect }}</b> Problems</b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="text-right">Incorrect:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ nWrong }}</b> Problems</b-col>
      </b-row>
      <b-row class="my-4">
        <b-col cols="6" class="text-right">Score:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ getScore() }}</b> %</b-col>
      </b-row>
      <br>
    </div>
  </div>
</template>

<script>
import '@/common/css/operation.css'
import * as uiUtil from '@/common/js/uiutil.js'

import katex from 'katex'
import FunctionTable from '@/math/elements/FunctionTable.vue'
import FunctionGraph from '@/math/elements/FunctionGraph.vue'

// required config: nProblems, maxPlaces
export default {
  name: 'SolveSimpleInput',
  props: [ 'myDrill' ],
  components: { FunctionTable, FunctionGraph },
  data () {
    return {
      // status
      iProblem: 0,
      nCorrect: 0,
      nWrong: 0,
      isDone: false,

      // problem
      problem: null,

      // user input & responses
      userInputs: null,
      inputAllowed: true,
      inputStatus: null,
      btnCheckStatus: null,
      btnGotitStatus: null,
      messages: null
    }
  },
  created () {
    this.newProblem()
  },
  computed: {
    inputWidth: function () {
      return this.myDrill.config.maxPlaces * 0.8
    }
  },
  watch: {
    isDone: function () {
      if (this.isDone) {
        this.$nextTick(() => this.$emit('completed', this.getScore()))
      }
    }
  },
  methods: {
    newProblem: function () {
      if (this.iProblem >= this.myDrill.config.nProblems) {
        this.isDone = true
        return false
      }

      // new problem
      this.iProblem = this.iProblem + 1
      this.problem = this.myDrill.problem(this.iProblem)

      // initialize input
      this.userInputs = new Array(this.problem.answers.length).fill(null)
      this.inputAllowed = true
      this.inputStatus = null
      this.btnCheckStatus = 'd-inline'
      this.btnGotitStatus = 'd-none'

      // initialize message
      this.messages = [null]

      // set the focus the first input box
      this.$nextTick(() => this.$el.querySelector('#userInput0').focus())

      return true
    },
    submitAnswer: function () {
      // require at least one input
      if (uiUtil.isEmpty(this.userInputs[0])) {
        this.$nextTick(() => this.$el.querySelector('#userInput0').focus())
        return false
      }

      this.inputAllowed = false

      if (this.isAnswer()) {
        this.nCorrect = this.nCorrect + 1
        this.inputStatus = 'input-correct'
        this.messages[0] = uiUtil.msgCorrect()
        setTimeout(() => this.newProblem(), 1000)
      } else {
        this.nWrong = this.nWrong + 1
        this.inputStatus = 'input-wrong'
        this.btnCheckStatus = 'd-none'
        this.btnGotitStatus = 'd-inline'
        this.messages[0] = uiUtil.msgWrong()
        this.messages = this.messages.concat(this.problem.explanation)

        this.$nextTick(() => this.$refs.btnGotit.focus())
      }

      return true
    },
    moveTo: function (index) {
      if (index < 0 || index >= this.problem.answers.length) return

      var elementName = '#userInput' + index
      this.$nextTick(() => this.$el.querySelector(elementName).focus())
    },
    isAnswer: function () {
      try {
        for (var i = 0; i < this.problem.answers.length; i++) {
          let userAnswer = this.userInputs[i].trim()

          // a single value is possible
          if (this.problem.answers[i].hasOwnProperty('value')) {
            if (this.problem.answers[i].value.toString() !== userAnswer) return false
          // multiple values are possible
          } else {
            if (!this.problem.answers[i].values.find(el => (el.toString() === userAnswer))) return false
          }
        }
        return true
      } catch (error) {
        return false
      }
    },
    getScore: function () {
      return Math.ceil(100 * this.nCorrect / this.myDrill.config.nProblems)
    },
    renderTex: function (str) {
      try {
        var texString = str.substring(str.indexOf('$') + 1, str.lastIndexOf('$'))
        return (!texString) ? str : str.replace('$' + texString + '$', katex.renderToString(texString))
      } catch (error) {
        return str
      }
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/math/operations/SolveSimpleInput.vue